import { floatify } from 'lib/maths/mathUtils'
import { sum } from 'lib/array/arrayUtils'
import calculateAdjustedEarnTotals from './calculateAdjustedEarnTotals'

export interface BurnAdjustedItemEarn {
  points: number
  basePoints: number
  bonusPoints: number
  statusCredits: number
  bonusStatusCredits: number
}

export interface BurnAdjustedEarnTotals extends BurnAdjustedItemEarn {
  remainingCash: number
}

function getRemainingCash(totalRequestPrice: number, pointsToApply: number, priceInPoints: number): number {
  const totalPointsApplied = Math.min(pointsToApply, priceInPoints)
  const pointsCashValue = floatify((totalPointsApplied * totalRequestPrice) / priceInPoints)
  return Math.max(0, totalRequestPrice - pointsCashValue)
}

/**
 * Recalculates each item's burn-adjusted earn, returning a result for every input calculation in the
 * same order. Mirrors the per-item logic of {@link recalculatePointsEarnAfterBurn} so the breakdown
 * modal can show the same reduced figures the summary tile displays once points are applied.
 */
export function recalculatePointsEarnAfterBurnPerItem(
  pointsCalculations: Array<App.LoyaltyPointsEarnCalculation>,
  pointsToApply: number,
  priceInPoints: number,
): Array<BurnAdjustedItemEarn> {
  const totalRequestPrice = sum(pointsCalculations, calc => calc.request.price ?? 0)

  if (pointsToApply <= 0 || priceInPoints <= 0 || totalRequestPrice <= 0) {
    return pointsCalculations.map(calc => ({
      points: calc.points,
      basePoints: calc.basePoints,
      bonusPoints: calc.bonusPoints,
      statusCredits: calc.statusCredits,
      bonusStatusCredits: calc.bonusStatusCredits,
    }))
  }

  const remainingCash = getRemainingCash(totalRequestPrice, pointsToApply, priceInPoints)
  const remainingRatio = remainingCash / totalRequestPrice

  return pointsCalculations.map(pointsCalculation => {
    const defaultPayableItemPrice = pointsCalculation.request.price ?? 0
    const adjustedTotals = calculateAdjustedEarnTotals({
      defaultPayableItemPrice,
      adjustedItemPrice: defaultPayableItemPrice * remainingRatio,
      basePoints: pointsCalculation.basePoints,
      bonusPoints: pointsCalculation.bonusPoints,
      statusCredits: pointsCalculation.statusCredits,
      bonusStatusCredits: pointsCalculation.bonusStatusCredits,
      roundingEnabled: pointsCalculation.roundingEnabled,
    })

    // `calculateAdjustedEarnTotals` re-derives status credits from the reduced base points using
    // the program-wide points→credit ratio. For high points-earn items (e.g. tours) the backend's
    // full-price `statusCredits` is capped well below that ratio, so the re-derived figure can come
    // out *higher* than the un-burned value — making displayed credits jump up when points are
    // applied. Burning points can only ever reduce earn, so clamp to the full-price base. (Checkout
    // avoids this by passing `earnCap` into `calculateAdjustedEarnTotals`, which this path lacks.)
    const earnableStatusCredits = Math.min(adjustedTotals.earnableStatusCredits, pointsCalculation.statusCredits)
    const earnableBonusStatusCredits = Math.min(adjustedTotals.earnableBonusStatusCredits, pointsCalculation.bonusStatusCredits)

    return {
      points: adjustedTotals.earnablePoints,
      basePoints: adjustedTotals.earnableBasePoints,
      bonusPoints: adjustedTotals.earnableBonusPoints,
      statusCredits: earnableStatusCredits,
      bonusStatusCredits: earnableBonusStatusCredits,
    }
  })
}

export default function recalculatePointsEarnAfterBurn(
  pointsCalculations: Array<App.LoyaltyPointsEarnCalculation>,
  pointsToApply: number,
  priceInPoints: number,
): BurnAdjustedEarnTotals {
  const totalRequestPrice = sum(pointsCalculations, calc => calc.request.price ?? 0)
  const remainingCash = (pointsToApply <= 0 || priceInPoints <= 0 || totalRequestPrice <= 0)
    ? totalRequestPrice
    : getRemainingCash(totalRequestPrice, pointsToApply, priceInPoints)

  return recalculatePointsEarnAfterBurnPerItem(pointsCalculations, pointsToApply, priceInPoints)
    .reduce<BurnAdjustedEarnTotals>((totals, item) => ({
      points: totals.points + item.points,
      basePoints: totals.basePoints + item.basePoints,
      bonusPoints: totals.bonusPoints + item.bonusPoints,
      statusCredits: totals.statusCredits + item.statusCredits,
      bonusStatusCredits: totals.bonusStatusCredits + item.bonusStatusCredits,
      remainingCash,
    }), { points: 0, basePoints: 0, bonusPoints: 0, statusCredits: 0, bonusStatusCredits: 0, remainingCash })
}
